import {
    Email,
    MemberPreferredCommunicationChannel,
    MemberPreferredContactSlot,
} from "@albertine/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useRef, useState } from "react";
import { type Control, useForm, useWatch } from "react-hook-form";
import { z } from "zod";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { PREFERRED_CHANNEL_LABELS } from "@/utils/channel.util";
import { emptyToUndefined } from "@/utils/format.util";
import { FormField, FormSectionLabel } from "../../form-field/FormField";
import { useMemberProfile } from "../MemberProfileContext";
import { listChannelContactOptions } from "../profileUtils";
import { OwnedField } from "./FormFields";
import { FormShell } from "./ProfileOverlayShell";
import type { FormProps } from "./types";

const CHANNEL_OPTIONS = MemberPreferredCommunicationChannel.options.map((id) => ({
    id,
    label: PREFERRED_CHANNEL_LABELS[id],
}));

const contactFormSchema = z.object({
    phoneNumber: z.string(),
    secondaryPhone: z.string(),
    email: Email,
    // Optional, so allow it to be cleared; otherwise it must be a valid email.
    secondaryEmail: Email.or(z.literal("")),
    preferredCommunicationChannel: z.string(),
    address: z.string(),
    postalCode: z.string(),
    city: z.string(),
    state: z.string(),
    country: z.string(),
    bin: z.string(),
    last4: z
        .string()
        .trim()
        .refine((value) => value === "" || /^\d{4}$/.test(value), "Enter 4 digits"),
    channelContact: z.string(),
});

type ContactFormValues = z.infer<typeof contactFormSchema>;

export function ContactForm({ onClose }: FormProps) {
    const { profile, saveContact, isAdmin } = useMemberProfile();
    const { control, handleSubmit, setValue } = useForm<ContactFormValues>({
        resolver: zodResolver(contactFormSchema),
        defaultValues: {
            phoneNumber: profile.contact.phoneNumber ?? "",
            secondaryPhone: profile.contact.secondaryPhone ?? "",
            email: profile.contact.email ?? "",
            secondaryEmail: profile.contact.secondaryEmail ?? "",
            preferredCommunicationChannel:
                profile.contact.preferredCommunicationChannel ?? "",
            address: profile.contact.address ?? "",
            postalCode: profile.contact.postalCode ?? "",
            city: profile.contact.city ?? "",
            state: profile.contact.state ?? "",
            country: profile.contact.country ?? "",
            bin: profile.contact.eligibilityDetails?.bin ?? "",
            last4: profile.contact.eligibilityDetails?.last4 ?? "",
            channelContact: profile.contact.channelContact ?? "",
        },
    });

    const watched = useWatch({ control });
    const channelChoices = listChannelContactOptions(
        watched.preferredCommunicationChannel ?? "",
        {
            phoneNumber: watched.phoneNumber ?? "",
            secondaryPhone: watched.secondaryPhone ?? "",
            email: watched.email ?? "",
            secondaryEmail: watched.secondaryEmail ?? "",
        },
    );

    const channel = watched.preferredCommunicationChannel ?? "";
    const previousChannel = useRef(channel);
    useEffect(() => {
        const channelSwitched = channel !== previousChannel.current;
        previousChannel.current = channel;
        if (channelChoices.length > 0 && (channelSwitched || !watched.channelContact)) {
            setValue("channelContact", "primary");
        }
    }, [channel, channelChoices.length, watched.channelContact, setValue]);

    function onSubmit(values: ContactFormValues) {
        const preferredChannel = MemberPreferredCommunicationChannel.safeParse(
            emptyToUndefined(values.preferredCommunicationChannel),
        );
        const submitChoices = listChannelContactOptions(
            values.preferredCommunicationChannel,
            values,
        );
        const channelContact = MemberPreferredContactSlot.safeParse(
            values.channelContact,
        );
        const bin = emptyToUndefined(values.bin);
        const last4 = emptyToUndefined(values.last4);

        saveContact(
            {
                email: emptyToUndefined(values.email),
                phoneNumber: emptyToUndefined(values.phoneNumber),
                secondaryEmail: emptyToUndefined(values.secondaryEmail),
                secondaryPhone: emptyToUndefined(values.secondaryPhone),
                preferredCommunicationChannel: preferredChannel.success
                    ? preferredChannel.data
                    : undefined,
                channelContact:
                    submitChoices.some(
                        (choice) => choice.id === values.channelContact,
                    ) && channelContact.success
                        ? channelContact.data
                        : undefined,
                address: emptyToUndefined(values.address),
                postalCode: emptyToUndefined(values.postalCode),
                city: emptyToUndefined(values.city),
                state: emptyToUndefined(values.state),
                country: emptyToUndefined(values.country),
                timeZone: profile.contact.timeZone,
                eligibilityDetails: bin || last4 ? { bin, last4 } : undefined,
            },
            onClose,
        );
    }

    return (
        <FormShell
            title="Edit contact details"
            onClose={onClose}
            onSave={handleSubmit(onSubmit)}
        >
            <RevealableField
                id="contact-phone"
                label="Phone"
                name="phoneNumber"
                control={control}
                disabled={!isAdmin}
                initiallyPresent={!!profile.contact.phoneNumber}
            />
            <OwnedField
                id="contact-secondary-phone"
                label="Secondary phone"
                kind="text"
                control={control}
                name="secondaryPhone"
            />
            <RevealableField
                id="contact-email"
                label="Email"
                name="email"
                control={control}
                disabled={!isAdmin}
                initiallyPresent={!!profile.contact.email}
            />
            <OwnedField
                id="contact-secondary-email"
                label="Secondary email"
                kind="text"
                control={control}
                name="secondaryEmail"
            />
            <OwnedField
                id="contact-preferred-channel"
                label="Preferred channel"
                kind="select"
                options={CHANNEL_OPTIONS}
                control={control}
                name="preferredCommunicationChannel"
            />
            {channelChoices.length > 0 && (
                <OwnedField
                    id="contact-channel-contact"
                    label={channelContactLabel(
                        watched.preferredCommunicationChannel ?? "",
                    )}
                    kind="select"
                    options={channelChoices}
                    control={control}
                    name="channelContact"
                />
            )}
            <OwnedField
                id="contact-address"
                label="Address"
                kind="text"
                control={control}
                name="address"
            />
            <OwnedField
                id="contact-postal-code"
                label="Postal code"
                kind="text"
                control={control}
                name="postalCode"
            />
            <OwnedField
                id="contact-city"
                label="City"
                kind="text"
                control={control}
                name="city"
            />
            <OwnedField
                id="contact-state"
                label="State"
                kind="text"
                control={control}
                name="state"
            />
            <OwnedField
                id="contact-country"
                label="Country"
                kind="text"
                control={control}
                name="country"
            />
            <FormSectionLabel className="mt-1">Eligibility check</FormSectionLabel>
            <OwnedField
                id="contact-bin"
                label="BIN"
                kind="text"
                control={control}
                name="bin"
            />
            <OwnedField
                id="contact-last4"
                label="Last 4 digits"
                kind="text"
                control={control}
                name="last4"
                inputMode="numeric"
                maxLength={4}
            />
        </FormShell>
    );
}

function RevealableField({
    id,
    label,
    name,
    control,
    disabled,
    initiallyPresent,
}: {
    id: string;
    label: string;
    name: "phoneNumber" | "email";
    control: Control<ContactFormValues>;
    disabled: boolean;
    initiallyPresent: boolean;
}) {
    const [revealed, setRevealed] = useState(initiallyPresent);

    if (!revealed) {
        return (
            <FormField label={label}>
                <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-9 justify-start px-0 hover:bg-transparent"
                    disabled={disabled}
                    onClick={() => setRevealed(true)}
                >
                    <Badge variant="secondary" size="sm">
                        Missing
                    </Badge>
                </Button>
            </FormField>
        );
    }

    return (
        <OwnedField id={id} label={label} kind="text" control={control} name={name} />
    );
}

function channelContactLabel(channel: string): string {
    switch (channel) {
        case "whatsapp":
            return "WhatsApp number";
        case "phone-call":
            return "Phone number";
        case "email":
            return "Email address";
        default:
            return "";
    }
}
